import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Container,
  Input,
  Button,
  VStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Spinner,
  Select,
  Checkbox,
  Card,
  CardBody,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Text,
  FormHelperText,
  Alert,
  AlertIcon,
  HStack,
  Divider,
  Badge,
  Box,
  SimpleGrid,
  Switch,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Tooltip,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { 
  FiMapPin, 
  FiGlobe, 
  FiStar, 
  FiPlus, 
  FiTrash2, 
  FiMoreVertical,
  FiCheck,
  FiX
} from 'react-icons/fi'
import { Link as RouterLink } from 'react-router-dom'
import apiClient from '../../../../utils/apiClient'

const EditLocale = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Modal controls
  const { isOpen: isAssignModalOpen, onOpen: onAssignModalOpen, onClose: onAssignModalClose } = useDisclosure()

  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0] || null

  const [locale, setLocale] = useState({
    id: null,
    tenant_id: tenantId,
    name: '',
    code: '',
    native_name: '',
    assignedSites: [] // Sites with locale assignments
  })
  const [availableSites, setAvailableSites] = useState([]) // Sites not yet assigned
  const [selectedNewSites, setSelectedNewSites] = useState([])
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [actionLoading, setActionLoading] = useState({})
  const [localeCodes, setLocaleCodes] = useState([])

  useEffect(() => {
    const fetchLocale = async () => {
      try {
        const data = await apiClient(`/locales/${tenantId}/${id}`)
        setLocale({
          id: data.id,
          tenant_id: tenantId,
          name: data.name,
          code: data.code,
          native_name: data.native_name || '',
          assignedSites: data.sites || []
        })
      } catch (error) {
        toast({
          title: 'Error fetching locale',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      }
    }

    const fetchAvailableSites = async () => {
      try {
        const response = await apiClient(`/locales/${tenantId}/${id}/available-sites`)
        setAvailableSites(response)
      } catch (error) {
        toast({
          title: 'Error fetching available sites',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      }
    }

    const fetchLocaleCodes = async () => {
      try {
        const response = await fetch('https://cdn.simplelocalize.io/public/v1/locales')
        const data = await response.json()
        setLocaleCodes(data)
      } catch (error) {
        toast({
          title: 'Error fetching locale codes',
          description: 'Could not load locale codes',
          status: 'error',
          duration: 3000
        })
      }
    }

    const fetchData = async () => {
      await Promise.all([fetchLocale(), fetchAvailableSites(), fetchLocaleCodes()])
      setLoading(false)
    }

    fetchData()
  }, [tenantId, id])

  const handleLocaleInfoUpdate = async (e) => {
    e.preventDefault()
    
    if (isSubmitting) return
    
    setIsSubmitting(true)
    
    try {
      await apiClient(`/locales/${tenantId}/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: locale.name,
          code: locale.code,
          native_name: locale.native_name
        })
      })

      toast({
        title: 'Success',
        description: 'Locale information updated successfully',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update locale information',
        status: 'error',
        duration: 3000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setLocale(prev => ({ ...prev, [name]: value }))
  }

  const handleLocaleCodeChange = (e) => {
    const selectedCode = e.target.value
    const selectedLocale = localeCodes.find(lc => lc.locale === selectedCode)
    
    setLocale(prev => ({
      ...prev,
      code: selectedCode,
      name: selectedLocale ? selectedLocale.language.name : prev.name,
      native_name: selectedLocale ? (selectedLocale.language.nativeName || selectedLocale.language.name) : prev.native_name
    }))
  }

  const handleAssignSites = async () => {
    if (selectedNewSites.length === 0) return

    setActionLoading({ assign: true })
    
    try {
      const assignments = selectedNewSites.map(siteId =>
        apiClient(`/sites/${tenantId}/${siteId}/locales`, {
          method: 'POST',
          body: JSON.stringify({
            locale_id: parseInt(id),
            is_default: false,
            is_active: true
          })
        })
      )

      await Promise.all(assignments)

      // Refresh data
      const updatedLocale = await apiClient(`/locales/${tenantId}/${id}`)
      setLocale(prev => ({
        ...prev,
        assignedSites: updatedLocale.sites || []
      }))
      
      const updatedAvailableSites = await apiClient(`/locales/${tenantId}/${id}/available-sites`)
      setAvailableSites(updatedAvailableSites)

      setSelectedNewSites([])
      onAssignModalClose()

      toast({
        title: 'Success',
        description: `${selectedNewSites.length} site(s) assigned successfully`,
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to assign sites',
        status: 'error',
        duration: 3000
      })
    } finally {
      setActionLoading({ assign: false })
    }
  }

  const handleRemoveSiteAssignment = async (siteId) => {
    setActionLoading({ [`remove_${siteId}`]: true })
    
    try {
      await apiClient(`/sites/${tenantId}/${siteId}/locales/${id}`, {
        method: 'DELETE'
      })

      // Refresh data
      const updatedLocale = await apiClient(`/locales/${tenantId}/${id}`)
      setLocale(prev => ({
        ...prev,
        assignedSites: updatedLocale.sites || []
      }))
      
      const updatedAvailableSites = await apiClient(`/locales/${tenantId}/${id}/available-sites`)
      setAvailableSites(updatedAvailableSites)

      toast({
        title: 'Success',
        description: 'Site assignment removed successfully',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove site assignment',
        status: 'error',
        duration: 3000
      })
    } finally {
      setActionLoading({ [`remove_${siteId}`]: false })
    }
  }

  const handleToggleDefault = async (siteId) => {
    setActionLoading({ [`default_${siteId}`]: true })
    
    try {
      await apiClient(`/sites/${tenantId}/${siteId}/locales/${id}/set-default`, {
        method: 'PUT'
      })

      // Refresh data
      const updatedLocale = await apiClient(`/locales/${tenantId}/${id}`)
      setLocale(prev => ({
        ...prev,
        assignedSites: updatedLocale.sites || []
      }))

      toast({
        title: 'Success',
        description: 'Default locale setting updated',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update default setting',
        status: 'error',
        duration: 3000
      })
    } finally {
      setActionLoading({ [`default_${siteId}`]: false })
    }
  }

  const handleToggleActive = async (siteId) => {
    setActionLoading({ [`active_${siteId}`]: true })
    
    try {
      await apiClient(`/sites/${tenantId}/${siteId}/locales/${id}/toggle-active`, {
        method: 'PUT'
      })

      // Refresh data
      const updatedLocale = await apiClient(`/locales/${tenantId}/${id}`)
      setLocale(prev => ({
        ...prev,
        assignedSites: updatedLocale.sites || []
      }))

      toast({
        title: 'Success',
        description: 'Active status updated',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update active status',
        status: 'error',
        duration: 3000
      })
    } finally {
      setActionLoading({ [`active_${siteId}`]: false })
    }
  }

  const getAssignmentStats = () => {
    const total = locale.assignedSites.length
    const active = locale.assignedSites.filter(site => site.is_active).length
    const defaults = locale.assignedSites.filter(site => site.is_default).length
    return { total, active, defaults }
  }

  const stats = getAssignmentStats()
  const isFormValid = locale.name.trim() && locale.code.trim()

  if (loading) {
    return (
      <Container maxW="container.lg" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading locale information...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings">Settings</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings/locales">Locales</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <HStack justify="space-between" align="center" mb={2}>
            <HStack>
              <Icon as={FiMapPin} color="purple.500" boxSize={6} />
              <Heading size="lg">Edit Locale</Heading>
            </HStack>
            <HStack>
              <Badge colorScheme="purple" variant="subtle">
                {locale.code}
              </Badge>
              <Badge colorScheme="blue" variant="outline">
                ID: {locale.id}
              </Badge>
            </HStack>
          </HStack>
          <Text color="gray.500">
            Update locale information and manage site assignments with specific settings.
          </Text>
        </Box>

        {/* Locale Information Card */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <form onSubmit={handleLocaleInfoUpdate}>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading size="md" mb={4}>Locale Information</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Locale Name</FormLabel>
                      <Input
                        name="name"
                        value={locale.name}
                        onChange={handleInputChange}
                        placeholder="Enter locale name"
                      />
                      <FormHelperText>
                        The display name for this locale (e.g., "English", "Français")
                      </FormHelperText>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Locale Code</FormLabel>
                      <Select name="code" value={locale.code} onChange={handleLocaleCodeChange}>
                        <option value="" disabled>Select a locale code</option>
                        {localeCodes.map((lc) => (
                          <option key={lc.locale} value={lc.locale}>
                            {lc.language.name} ({lc.locale})
                          </option>
                        ))}
                      </Select>
                      <FormHelperText>
                        The ISO language code for this locale (e.g., "en-US", "fr-FR")
                      </FormHelperText>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl mt={4}>
                    <FormLabel>Native Name</FormLabel>
                    <Input
                      name="native_name"
                      value={locale.native_name}
                      onChange={handleInputChange}
                      placeholder="Enter native name (e.g., Français for French)"
                    />
                    <FormHelperText>
                      The name of the language in its native script
                    </FormHelperText>
                  </FormControl>
                </Box>

                <Divider />

                <HStack justify="flex-end">
                  <Button
                    type="submit"
                    colorScheme="blue"
                    isDisabled={!isFormValid}
                    isLoading={isSubmitting}
                    loadingText="Updating..."
                  >
                    Update Locale Information
                  </Button>
                </HStack>
              </VStack>
            </form>
          </CardBody>
        </Card>

        {/* Site Assignments Card */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <VStack spacing={6} align="stretch">
              <HStack justify="space-between" align="center">
                <HStack>
                  <Icon as={FiGlobe} color="blue.500" />
                  <Heading size="md">Site Assignments</Heading>
                </HStack>
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  onClick={onAssignModalOpen}
                  isDisabled={availableSites.length === 0}
                >
                  Assign to Sites
                </Button>
              </HStack>

              {/* Assignment Statistics */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Card variant="outline" size="sm">
                  <CardBody>
                    <Stat>
                      <StatLabel>Total Sites</StatLabel>
                      <StatNumber color="blue.500">{stats.total}</StatNumber>
                      <StatHelpText>Assigned to this locale</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card variant="outline" size="sm">
                  <CardBody>
                    <Stat>
                      <StatLabel>Active</StatLabel>
                      <StatNumber color="green.500">{stats.active}</StatNumber>
                      <StatHelpText>Currently enabled</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card variant="outline" size="sm">
                  <CardBody>
                    <Stat>
                      <StatLabel>Default</StatLabel>
                      <StatNumber color="yellow.500">{stats.defaults}</StatNumber>
                      <StatHelpText>Set as fallback</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Current Site Assignments */}
              {locale.assignedSites && locale.assignedSites.length > 0 ? (
                <Box>
                  <Text fontWeight="medium" mb={3}>Current Assignments</Text>
                  <VStack spacing={3} align="stretch">
                    {locale.assignedSites.map(site => (
                      <Card key={site.site_id} variant="outline" size="sm">
                        <CardBody>
                          <HStack justify="space-between" align="center">
                            <HStack spacing={3}>
                              <VStack align="start" spacing={1}>
                                <HStack spacing={1} align="center">
                                <Text fontWeight="medium" fontSize="sm">
                                  {site.site_name}
                                </Text>
                                <Tooltip label={site.is_default ? 'Remove as Default' : 'Set as Default'}>
                                <IconButton
                                  icon={<FiStar />}
                                  variant="ghost"
                                  size="xs"
                                  color={site.is_default ? 'yellow.500' : 'gray.400'}
                                  onClick={() => handleToggleDefault(site.site_id)}
                                  isLoading={actionLoading[`default_${site.site_id}`]}
                                  aria-label={site.is_default ? 'Remove as Default' : 'Set as Default'}
                                />
                                </Tooltip>
                                </HStack>
                                <HStack spacing={2}>
                                  {site.is_default && (
                                    <Badge size="xs" colorScheme="yellow" variant="solid">
                                      <Icon as={FiStar} mr={1} />
                                      Default
                                    </Badge>
                                  )}
                                  <Badge 
                                    size="xs" 
                                    colorScheme={site.is_active ? 'green' : 'gray'}
                                    variant="outline"
                                  >
                                    {site.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </HStack>
                              </VStack>
                            </HStack>

                            <HStack spacing={2}>
                              <Tooltip label={site.is_active ? 'Deactivate' : 'Activate'}>
                                <Switch
                                  isChecked={site.is_active}
                                  onChange={() => handleToggleActive(site.site_id)}
                                  isDisabled={actionLoading[`active_${site.site_id}`]}
                                  colorScheme="green"
                                  size="sm"
                                />
                              </Tooltip>

                              <Menu>
                                <MenuButton
                                  as={IconButton}
                                  icon={<FiMoreVertical />}
                                  variant="ghost"
                                  size="sm"
                                  isLoading={
                                    actionLoading[`default_${site.site_id}`] || 
                                    actionLoading[`remove_${site.site_id}`]
                                  }
                                />
                                <MenuList>
                                  {!site.is_default && (
                                    <MenuItem
                                      icon={<FiStar />}
                                      onClick={() => handleToggleDefault(site.site_id)}
                                    >
                                      Set as Default
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    icon={<FiTrash2 />}
                                    color="red.500"
                                    onClick={() => handleRemoveSiteAssignment(site.site_id)}
                                  >
                                    Remove Assignment
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </HStack>
                          </HStack>
                        </CardBody>
                      </Card>
                    ))}
                  </VStack>
                </Box>
              ) : (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">No Site Assignments</Text>
                    <Text fontSize="sm">
                      This locale is not assigned to any sites yet. Assign it to sites to make it available for content.
                    </Text>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Assign Sites Modal */}
      <Modal isOpen={isAssignModalOpen} onClose={onAssignModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Locale to Sites</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.500" fontSize="sm">
                Select sites to assign this locale to. Each assignment will be active by default.
              </Text>
              
              {availableSites.length > 0 ? (
                <VStack spacing={2} align="stretch">
                  {availableSites.map(site => (
                    <Card
                      key={site.id}
                      variant="outline"
                      size="sm"
                      borderColor={selectedNewSites.includes(site.id.toString()) ? "blue.200" : borderColor}
                      bg={selectedNewSites.includes(site.id.toString()) ? "blue.50" : "transparent"}
                    >
                      <CardBody p={3}>
                        <Checkbox
                          isChecked={selectedNewSites.includes(site.id.toString())}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedNewSites(prev => [...prev, site.id.toString()])
                            } else {
                              setSelectedNewSites(prev => prev.filter(id => id !== site.id.toString()))
                            }
                          }}
                          w="full"
                        >
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" fontSize="sm">
                              {site.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {site.domain || 'No domain set'}
                            </Text>
                          </VStack>
                        </Checkbox>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Text>This locale is already assigned to all available sites.</Text>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleAssignSites}
              isDisabled={selectedNewSites.length === 0}
              isLoading={actionLoading.assign}
              loadingText="Assigning..."
            >
              Assign Selected Sites
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  )
}

export default EditLocale
